import { createContext, useContext, type ReactNode } from 'react';
import {
  useQuery,
  type QueryKey,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  Card,
  Nullable,
  UserWithToken,
} from '@ygoargentina/sdk/api/api.types';
import type SDK from '@ygoargentina/sdk/api';

// Config extra de los pedidos REST (import de planilla). Sale de la firma del
// SDK para no depender de axios desde ui.
export type CollectionRequestConfig = Parameters<typeof SDK.Boxes.syncList>[1];

// Misma firma que `useQuery` de un solo argumento: folder pasa su
// `useAuthQuery` (redirige al login ante un 401) y el resto usa el de
// react-query tal cual.
export type CollectionQueryHook = <
  TQueryFnData = unknown,
  TError = Error,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>
) => UseQueryResult<TData, TError>;

export type CardCatalog = { cards: Card[]; isLoading: boolean };

/**
 * Lo que la colección necesita de la app que la monta. La colección es la
 * misma en folder y en marketplace; esto es lo único que cambia entre las dos.
 */
export type CollectionConfig = {
  user: Nullable<UserWithToken>;
  /**
   * Catálogo completo de cartas para "Agregar cartas" y "Editar item". Es un
   * hook porque cada app lo cachea a su manera (folder lo persiste en un
   * store). Tiene que ser una referencia estable.
   */
  useCardCatalog: () => CardCatalog;
  /** Ruta de la ficha de una carta (`/cards/:id` en folder, `/cartas/:id` en marketplace). */
  cardHref: (konamiId: number) => string;
  /** Configuración completa del vendedor. Puede ser una URL absoluta a otra app. */
  sellerSettingsHref: string;
  /** Headers extra para los pedidos REST. */
  requestConfig?: () => CollectionRequestConfig;
  /** Reemplaza a `useQuery` (ej. para manejar el 401). Tiene que ser estable. */
  useQuery?: CollectionQueryHook;
};

const CollectionContext = createContext<CollectionConfig | null>(null);

export function CollectionProvider({
  value,
  children,
}: {
  value: CollectionConfig;
  children: ReactNode;
}) {
  return (
    <CollectionContext.Provider value={value}>
      {children}
    </CollectionContext.Provider>
  );
}

export function useCollectionConfig(): CollectionConfig {
  const config = useContext(CollectionContext);
  if (!config) {
    throw new Error('useCollectionConfig requiere un <CollectionProvider>');
  }
  return config;
}

const useDefaultQuery: CollectionQueryHook = (options) => useQuery(options);

// `useQuery` de la colección: el que inyectó la app o el de react-query.
export const useCollectionQuery: CollectionQueryHook = (options) => {
  const { useQuery: injected } = useCollectionConfig();
  return (injected ?? useDefaultQuery)(options);
};
